import React from 'react'
import get from 'lodash/get'
import { Card } from 'blocket-ui'
import queryString from 'query-string'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import ReactPlaceholder from 'react-placeholder'
import styleguide from 'styleguide'
import { ie11media, mediaQueries, withDevice } from '@blazar-plugins/device'
import EducationPlacement from 'external/BlocketUtbildning/EducationPlacement'

import TabBar from 'components/TabBar'
import ErrorComponent from 'components/ErrorComponent'
import Pagination from 'components/Pagination'
import Placement from 'components/Placement'
import QuickLinks from 'components/QuickLinks'
import Footer from 'components/Footer'
import Toplistings from 'external/Toplistings'
import SkipLink from 'components/SkipLink'
import { OnboardingBanner } from 'components/OnboardingBanner'
import { FullHeightWrapper } from 'components/LayoutStyles'
import JobPuffs from 'components/JobPuffs'
import IPCPlacement from 'components/IPCPlacement'

import { PlaceholderWrapper } from 'utils/LoadingAnimationStyles'
import SearchHeaderResponsive from './SearchHeaderResponsive'
import SearchResults from './SearchResults'
import EmptyStateList from './EmptyStateList'
import ShippingAds from './ShippingAds'
import FilterSave from './FilterSave'
import Gallery from './Gallery'
import ArchivedAdNotification from './ArchivedAdNotification'
import GoogleAdsense from './GoogleAdsense/GoogleAdsense'
import BostadRentLink from './BostadRentLink'

const StyledCard = styled(Card)`
  margin-bottom: 16px;
`

const Container = styled(FullHeightWrapper)`
  justify-content: flex-start;
  flex: 1;
  background-color: ${styleguide.color.extraLightGrey};
`

const CenterWithPadding = styled.main`
  max-width: 980px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px;

  @media ${mediaQueries.large} {
    padding: 0;
  }
`

const BodyWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 250px;
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  padding: 16px 0;
  ${ie11media} {
    display: -ms-grid;
    -ms-grid-columns: 1fr 16px 250px;
    -ms-grid-rows: auto 16px 1fr;
  }
`

const BodyLeft = styled.div`
  grid-column: 1 / 2;
  ${ie11media} {
    -ms-grid-column: 1;
    -ms-grid-row: 3;
  }
`

const BodyRight = styled.div`
  grid-column: 2 / 3;
  ${ie11media} {
    -ms-grid-column: 3;
    -ms-grid-row: 3;
  }
`

const PaginationWrapper = styled.div`
  padding-top: 16px;
  padding-bottom: 56px;
  width: 100%;
`

const SearchHeaderWrapper = styled.div`
  background-color: ${styleguide.color.white};
  border-bottom: 1px solid #efefef;
`

const Sticky = styled.div`
  position: sticky;
  position: -webkit-sticky;
  top: ${(props) => props.top};
`

const StyledEmptySearchCard = styled(Card)`
  margin-top: 40px;
  padding: 60px 16px;
`

const FilterSaveWrapper = styled.div`
  margin-bottom: 16px;
`
const PuffWrapper = styled.aside`
  text-align: center;
`

const StyledArchivedAdNotification = styled(ArchivedAdNotification)`
  margin-top: 16px;
  border: 1px solid #dfdede;
`

const galleryPlaceholder = {
  height: '360px',
  marginTop: 0,
  marginBottom: '16px',
}

const placementHeight = { height: 240 }

export const MediumLayout = ({
  error,
  errorId,
  haveSearchResults,
  isFetching,
  searchTotal,
  totalPageCount,
  firstFetch,
  location,
  urls,
  inventory = {},
  searchResultDescription,
  hitsPerPage,
  breadcrumbs,
  searchParametersDefinition,
  searchParamsActions,
  searchSubsetDescription,
  searchInputActions,
  cgParameterDefinition,
  geoPref,
  device,
  classifiedActions,
  isRefreshEnabled,
  experimentalIPCEnabled,
  filterQuery,
  shippingAds,
}) => {
  const loading = firstFetch || isFetching
  const showEmptyState = !haveSearchResults && !loading
  const queryParams = queryString.parse(location.search)
  const currentPage = Number(get(queryParams, 'page', 1))
  const { sh } = searchParametersDefinition || {}

  const placements = [
    'placement_widescreen-1',
    'placement_widescreen-2',
    'placement_widescreen-3',
    'placement_widescreen-4',
  ]

  if (error) {
    return <ErrorComponent error={errorId} />
  }

  const child = (
    <>
      <BostadRentLink cg={queryParams.cg} />
      {queryParams.cg === '7093' && <EducationPlacement baseUrl={urls.desktop} />}
      {sh && <OnboardingBanner />}
      {queryParams.cg === '1020' && (
        <OnboardingBanner
          isPayment
          cookieKey="hidePaymentOnboarding"
          title="Trygg betalning för alla!"
          text="Nu gör vi det tryggare och smidigare att köpa och sälja på Blocket."
          readmoreText="Läs mer om Blocketbetalning"
          readmoreUrl="/trygg-betalning-pa-blocket"
        />
      )}
      <PlaceholderWrapper>
        <ReactPlaceholder
          type="textRow"
          color="#fff"
          style={{ height: device.is('large') ? 240 : 180 }}
          ready={!loading}
          showLoadingAnimation
        >
          <Placement
            id="placement_panorama"
            inventory={inventory}
            style={placementHeight}
            path="list"
          />
        </ReactPlaceholder>
      </PlaceholderWrapper>
      <StyledArchivedAdNotification />
      <BodyWrapper>
        <BodyLeft>
          <FilterSaveWrapper>
            <FilterSave
              searchResultDescription={searchResultDescription}
              location={location}
              loading={loading}
              device={device}
            />
          </FilterSaveWrapper>
          {haveSearchResults && !loading && <Toplistings />}
          {showEmptyState ? (
            <>
              <StyledEmptySearchCard>
                <EmptyStateList
                  shouldLinkSearch={false}
                  device={device}
                  shippingAds={shippingAds}
                />
              </StyledEmptySearchCard>
              <GoogleAdsense
                layout="medium"
                emptySearchResults={!haveSearchResults}
                label="Kanske intressant istället?"
              />
            </>
          ) : (
            <SearchResults
              layout="medium"
              loading={loading}
              device={device}
              totalPageCount={totalPageCount}
              currentPage={currentPage}
            />
          )}
          <ShippingAds layout="medium" loading={loading} device={device} />
          {!showEmptyState && (
            <>
              <GoogleAdsense
                device={device}
                layout="medium"
                label="Liknande produkter från Google"
              />
              <PaginationWrapper>
                <Pagination
                  classifiedActions={classifiedActions}
                  totalPageCount={totalPageCount}
                  currentPage={Number(get(queryParams, 'page', 1))}
                  hitCount={searchTotal}
                  hitsPerPage={hitsPerPage}
                  showRefreshButton={isRefreshEnabled}
                />
              </PaginationWrapper>
            </>
          )}
        </BodyLeft>
        <BodyRight>
          <PlaceholderWrapper>
            <ReactPlaceholder
              type="textRow"
              ready={!loading}
              color="#fff"
              style={galleryPlaceholder}
              showLoadingAnimation
            >
              <Gallery />
            </ReactPlaceholder>
            <ReactPlaceholder
              type="textRow"
              ready={!loading}
              color="#fff"
              style={galleryPlaceholder}
              showLoadingAnimation
            >
              {placements.map((placement) => (
                <Placement key={placement} id={placement} inventory={inventory} path="list" />
              ))}
              {experimentalIPCEnabled ? (
                <StyledCard>
                  <IPCPlacement />
                </StyledCard>
              ) : (
                <JobPuffs type="li" location={location} />
              )}
            </ReactPlaceholder>
          </PlaceholderWrapper>
          {!loading && (
            <Sticky top="16px">
              <Placement id="placement_widescreen-sticky" inventory={inventory} path="list" />
            </Sticky>
          )}
        </BodyRight>
      </BodyWrapper>
    </>
  )

  return (
    <Container>
      <SkipLink to="#skip-tabbar" />
      <TabBar />
      <SearchHeaderWrapper id="skip-tabbar">
        <SearchHeaderResponsive
          breadcrumbs={breadcrumbs}
          searchParametersDefinition={searchParametersDefinition}
          searchParamsActions={searchParamsActions}
          searchSubsetDescription={searchSubsetDescription}
          searchInputActions={searchInputActions}
          cgParameterDefinition={cgParameterDefinition}
          geoPref={geoPref}
          isFetching={isFetching}
          filterQuery={filterQuery}
        />
      </SearchHeaderWrapper>
      <CenterWithPadding>{child}</CenterWithPadding>
      {!loading && (
        <>
          <PuffWrapper>
            <JobPuffs type="li_footer" location={location} />
          </PuffWrapper>

          <QuickLinks inventory={inventory} />

          <Footer />
        </>
      )}
    </Container>
  )
}

MediumLayout.propTypes = {
  error: PropTypes.bool.isRequired,
  errorId: PropTypes.string,
  isFetching: PropTypes.bool.isRequired,
  haveSearchResults: PropTypes.bool,
  searchTotal: PropTypes.number.isRequired,
  totalPageCount: PropTypes.number.isRequired,
  hitsPerPage: PropTypes.number.isRequired,
  firstFetch: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  inventory: PropTypes.object,
  searchResultDescription: PropTypes.node,
  urls: PropTypes.object.isRequired,
  breadcrumbs: PropTypes.array,
  searchParametersDefinition: PropTypes.object,
  searchSubsetDescription: PropTypes.string,
  searchParamsActions: PropTypes.object.isRequired,
  cgParameterDefinition: PropTypes.shape({}),
  searchInputActions: PropTypes.shape({
    updateHistory: PropTypes.func.isRequired,
  }),
  device: PropTypes.shape({ is: PropTypes.func.isRequired }).isRequired,
  geoPref: PropTypes.object,
  classifiedActions: PropTypes.object,
  isRefreshEnabled: PropTypes.bool,
  experimentalIPCEnabled: PropTypes.bool,
  filterQuery: PropTypes.object,
  shippingAds: PropTypes.object.isRequired,
}

export default withDevice(MediumLayout)
